import type { DefaultError } from '../constants';
import type { IOperationError } from '../types';
import type {
  OpErrorBuilder,
  OpErrorBuilderFactoryParams,
  OpErrorDefinition,
} from './op-error-builder-factory.types';

function buildErrorFromCatalog<TError extends string>(
  errorCatalog: readonly OpErrorDefinition<TError>[],
  error: TError,
): IOperationError<DefaultError | TError> {
  const errorDefinition = errorCatalog.find((e) => e.error === error);

  if (errorDefinition)
    return {
      code: error,
    };

  return {
    code: 'unknownException',
  };
}

export function identifySingleErrorSource<TError extends string>(
  errorCatalog: readonly OpErrorDefinition<TError>[],
  errorSource: unknown,
  preferredError?: TError,
): IOperationError<DefaultError | TError> {
  if (errorSource == null)
    return {
      code: 'unknownException',
    };

  if (errorSource instanceof Error)
    return {
      code: preferredError ?? 'unexpectedException',
      rootCause: errorSource,
    };

  if (typeof errorSource === 'string')
    return buildErrorFromCatalog(errorCatalog, errorSource as TError);

  try {
    const sourceAsString = JSON.stringify(errorSource);
    return {
      code: preferredError ?? 'unexpectedException',
      rootCause: new Error(sourceAsString),
    };
  } catch {
    return {
      code: preferredError ?? 'unexpectedException',
      rootCause: new Error(`Unable to parse rootCause: ${String(errorSource)}`),
    };
  }
}

/**
 * opErrorBuilderFactory
 * @param parameters - This object implements the OpErrorBuilderFactoryParams interface. The following parameters are available:
 * parameters.errorCatalog - An array of OpErrorDefinition objects. This catalog is going to be used to type the resulting function.
 * @returns error builder function
 *
 * @example
 *
 * ```ts
 * const customErrorDefintion = [
 * {
 *   error: 'error1',
 *   description: 'some description for error 1',
 * },
 * {
 *   error: 'error2',
 *   description: 'some description for error 2',
 * },
 * {
 *   error: 'error3',
 *   description: 'some description for error 3',
 * },
 * ] as const;
 *
 * const errorBuilder = opErrorBuilderFactory({
 *     errorCatalog: customErrorDefintion,
 * });
 *
 * // It should build an error1 from catalog
 * const error = errorBuilder({
 *  add: 'error1',
 * });
 *
 * // It should build an error2 from catalog with an exception attached
 * const customError = new Error('CustomError');
 * const error = errorBuilder({
 *  add: 'error2',
 *  e: customError,
 * });
 *
 * // It should build an error3 from catalog with an IOperationError
 * const error = errorBuilder({
 * e: {
 *    code: 'error3',
 *  }
 * });
 *
 * // It should build an error 1 and add multiple errors
 * const customErrorA = new Error('CustomErrorA');
 * const customErrorB = new Error('CustomErrorB');
 * const error = errorBuilder({
 *  add: 'error1',
 *  e: [customErrorA, customErrorB],
 * });
 * ```
 */
export function opErrorBuilderFactory<TError extends string>({
  errorCatalog,
}: OpErrorBuilderFactoryParams<TError>): OpErrorBuilder<DefaultError | TError> {
  return ({ e, existingErrors, add }) => {
    const errors = existingErrors ?? [];

    if (e instanceof Array)
      e.forEach((error) =>
        errors.push(identifySingleErrorSource(errorCatalog, error)),
      );
    else if (e) errors.push(identifySingleErrorSource(errorCatalog, e, add));

    if (add && !errors.find((e) => e.code === add))
      errors.push(
        buildErrorFromCatalog(errorCatalog, add as NonNullable<TError>),
      );

    return errors;
  };
}
